// [Proposition: vue-jalons-taches] Client for /api/vue-jalons-taches
// (tree, milestone status) and /api/task-todos (to-do lists, used by every
// task screen). Task creation and column moves go through the regular task
// API (api/client.ts).

import type { TaskUpdate } from '../../types';

const BASE = '/api/vue-jalons-taches';
const TODOS_BASE = '/api/task-todos';

export interface TodoItem {
  id: number;
  label: string;
  description: string | null;
  /** Every saved version of the description, oldest first (by/at null for a text written before the history existed). */
  description_history?: { text: string; by: string | null; at: string | null }[];
  planned_date: string | null; // 'YYYY-MM-DD'
  due_date: string | null;     // 'YYYY-MM-DD'
  done: boolean;
  done_by: string | null;
  done_at: string | null;
  doc_count?: number; // [Proposition: documents]
  comment_count?: number; // comments in the item's own thread
}

export interface TreeTask {
  id: number;
  title: string;
  assignee_name: string | null;
  collaborator_names: string[]; // [Proposition: collaborateurs-tache]
  due_date: string | null;
  column_id: number;
  column_label: string;
  milestone_id: string | null;
  is_done: boolean;
  todos: TodoItem[];
  description: string | null;
  /** Every saved version of the description, oldest first (by/at null for a text written before the history existed). */
  description_history?: { text: string; by: string | null; at: string | null }[];
  planned_at: string | null;
  planned_minutes: number | null;
  is_private: boolean;
  can_open: boolean;
  /** Documents of the task, its to-do items and its comments (0 for a private task the viewer can't open). */
  doc_count?: number;
}

export interface TreeMilestone {
  id: string;
  name: string;
  status: string; // 'pending' | 'active' | 'completed' | 'cancelled'
  type: string | null; // 'process' → milestone handed to the CdE
  description: string;
  estimated_start: string | null;
  estimated_end: string | null;
  assignments: string[];
  deliverables: { id: string; title: string; body: string }[]; // linked deliverables
  // Cancelled: who, when, why — shown on its greyed line; null otherwise.
  cancellation: { at: string | null; by: string | null; reason: string | null } | null;
  archived_tasks: { id: number; title: string }[]; // a cancelled milestone's archived tasks
}

export interface ProjectTree {
  project: { id: number; name: string; project_code: string | null; status_label: string | null; execution_health: 'green' | 'orange' | 'red' | null };
  milestones: TreeMilestone[];
  tasks: TreeTask[];
  canCloseMilestones: boolean;
  // [Proposition: roles-projet] Team members' project role (null if not chosen yet) and précision.
  team: { name: string; project_role: string | null; precision: string }[];
}

async function request<T>(url: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(url, {
    ...options,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...options.headers },
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    // Keep the server's machine-readable code (e.g. TASK_DONE) for callers.
    throw Object.assign(new Error(error.error || 'Request failed'), { code: error.code as string | undefined });
  }
  return response.json();
}

export const vjtApi = {
  getTree: (projectId: number) => request<ProjectTree>(`${BASE}/projects/${projectId}`),
  setMilestoneStatus: (projectId: number, milestoneId: string, status: 'completed' | 'active') =>
    request<{ ok: true }>(`${BASE}/projects/${projectId}/milestones/${encodeURIComponent(milestoneId)}`, {
      method: 'PATCH', body: JSON.stringify({ status }),
    }),
  cancelMilestone: (projectId: number, milestoneId: string, reason: string) =>
    request<{ ok: true }>(`${BASE}/projects/${projectId}/milestones/${encodeURIComponent(milestoneId)}/cancel`, {
      method: 'POST', body: JSON.stringify({ reason }),
    }),
  restoreMilestone: (projectId: number, milestoneId: string) =>
    request<{ ok: true }>(`${BASE}/projects/${projectId}/milestones/${encodeURIComponent(milestoneId)}/restore`, { method: 'POST' }),
};

export const todoApi = {
  list: (taskId: number) => request<TodoItem[]>(`${TODOS_BASE}/tasks/${taskId}`),
  /** Rejects with code 'TASK_DONE' on a Done task unless `reopen` confirms reopening it. */
  add: (taskId: number, label: string, reopen = false) =>
    request<TodoItem & { reopened: boolean }>(`${TODOS_BASE}/tasks/${taskId}`, { method: 'POST', body: JSON.stringify({ label, reopen }) }),
  update: (id: number, patch: { label?: string; done?: boolean; description?: string; planned_date?: string | null; due_date?: string | null }) =>
    request<TodoItem & { due_clamped_to?: string | null; planned_clamped_to?: string | null }>(`${TODOS_BASE}/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  remove: (id: number) => request<{ ok: true }>(`${TODOS_BASE}/${id}`, { method: 'DELETE' }),
  /** The item's own comment thread (newest first); editing/deleting a comment goes through the task's comment routes. */
  comments: (id: number) => request<TaskUpdate[]>(`${TODOS_BASE}/${id}/comments`),
  addComment: (id: number, content: string, opts: { update_type?: string; reply_to_id?: number; tagged_user_ids?: number[] } = {}) =>
    request<TaskUpdate>(`${TODOS_BASE}/${id}/comments`, { method: 'POST', body: JSON.stringify({ content, ...opts }) }),
};
